import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import {
  OptimisticMessage,
  SupportChatService,
  ticketStatusKey,
  ticketStatusSeverity,
} from '@cosmos/angular/utils';
import { MessageBubbleComponent } from './message-bubble.component';
import { TicketListItemComponent } from './ticket-list-item.component';

/** Support email shown in the panel header. */
const SUPPORT_EMAIL = 'app@cosmosengineering.es';

/** A message row, optionally preceded by a day separator. */
interface ThreadRow {
  message: OptimisticMessage;
  daySeparator: string | null;
}

@Component({
  selector: 'cosmos-support-chat-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    FormsModule,
    TranslatePipe,
    ButtonModule,
    DividerModule,
    MessageModule,
    TagModule,
    TextareaModule,
    MessageBubbleComponent,
    TicketListItemComponent,
  ],
  templateUrl: './support-chat-panel.component.html',
})
export class SupportChatPanelComponent implements OnInit, OnDestroy {
  protected readonly supportChat = inject(SupportChatService);
  private readonly translate = inject(TranslateService);
  protected readonly supportEmail = SUPPORT_EMAIL;

  private readonly injector = inject(Injector);
  private readonly scrollViewport =
    viewChild<ElementRef<HTMLElement>>('scrollViewport');

  protected readonly draft = signal('');
  /** true = keep the thread pinned to the bottom as content changes.
   *  Flipped off when the user scrolls up to read history. */
  private stickBottom = true;
  /** guards against onThreadScroll() reacting to our own programmatic scroll. */
  private programmaticScroll = false;

  private readonly _isComposingNew = signal(false);

  constructor() {
    // Scroll to bottom whenever the rendered message set changes (thread open
    // loads messages async, a send appends one, a poll brings a reply) — but
    // only if the user hasn't scrolled up to read history. `rows()` only
    // changes in thread context, so this doesn't fire during list polling.
    effect(() => {
      const count = this.rows().length;
      const loading = this.supportChat.loadingThread();
      if (loading || count === 0 || !this.stickBottom) return;
      this.scrollToBottomAfterRender();
    });
  }

  /** Wait for Angular to paint the new rows, then jump the viewport to the
   *  bottom. A single afterNextRender can still miss late layout (images,
   *  fonts) so we also nudge on the next frame. */
  private scrollToBottomAfterRender(): void {
    afterNextRender(
      () => {
        const el = this.scrollViewport()?.nativeElement;
        if (!el) return;
        this.programmaticScroll = true;
        el.scrollTop = el.scrollHeight;
        requestAnimationFrame(() => {
          el.scrollTop = el.scrollHeight;
          this.programmaticScroll = false;
        });
      },
      { injector: this.injector },
    );
  }

  /** Called from the scroll container — remember whether we're pinned to the
   *  bottom so incoming messages don't yank the view away from history. */
  protected onThreadScroll(): void {
    if (this.programmaticScroll) return;
    const el = this.scrollViewport()?.nativeElement;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.stickBottom = distanceFromBottom < 80;
  }

  protected readonly view = computed<'list' | 'thread'>(() =>
    this.supportChat.activeTicketId() || this._isComposingNew()
      ? 'thread'
      : 'list',
  );

  protected readonly activeTicket = computed(() =>
    this.supportChat
      .tickets()
      .find((t) => t.id === this.supportChat.activeTicketId()),
  );

  protected readonly isResolved = computed(
    () => this.activeTicket()?.status === 'resolved',
  );

  protected readonly activeStatusKey = computed(() => {
    const s = this.activeTicket()?.status;
    return s ? ticketStatusKey(s) : null;
  });

  protected readonly activeStatusSeverity = computed(() => {
    const s = this.activeTicket()?.status;
    return s ? ticketStatusSeverity(s) : 'info';
  });

  /** Messages with day-separator markers injected on date boundaries. */
  protected readonly rows = computed<ThreadRow[]>(() => {
    const msgs = this.supportChat.messages();
    const out: ThreadRow[] = [];
    let prevDay: string | null = null;
    for (const message of msgs) {
      const day = this.dayKey(message.created_at);
      out.push({
        message,
        daySeparator: day !== prevDay ? this.dayLabel(message.created_at) : null,
      });
      prevDay = day;
    }
    return out;
  });

  ngOnInit(): void {
    this.supportChat.setPanelOpen(true);
    void this.supportChat.refreshTickets();

    const activeId = this.supportChat.activeTicketId();
    if (activeId) {
      this.draft.set(this.supportChat.readDraft(activeId));
      this.stickBottom = true;
      void this.supportChat.openTicket(activeId);
    } else {
      this.draft.set(this.supportChat.readDraft('new'));
    }
  }

  ngOnDestroy(): void {
    this.supportChat.setPanelOpen(false);
  }

  protected openTicket(id: string): void {
    this._isComposingNew.set(false);
    this.draft.set(this.supportChat.readDraft(id));
    this.stickBottom = true;
    void this.supportChat.openTicket(id);
  }

  protected backToList(): void {
    this._isComposingNew.set(false);
    this.supportChat.closeThread();
  }

  protected startNew(): void {
    this._isComposingNew.set(true);
    this.draft.set(this.supportChat.readDraft('new'));
    this.supportChat.startNewConversation();
  }

  protected onDraftChange(value: string): void {
    this.draft.set(value);
    const key = this.supportChat.activeTicketId() ?? 'new';
    this.supportChat.writeDraft(key, value);
  }

  protected async send(): Promise<void> {
    const text = this.draft().trim();
    if (!text) return;
    this.draft.set('');
    this.stickBottom = true;
    await this.supportChat.sendMessage(text);
    this._isComposingNew.set(false);
  }

  protected onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.send();
    }
  }

  protected retry(localId: string): void {
    void this.supportChat.retryMessage(localId);
  }

  // ---- day separators ----

  private dayKey(iso: string): string {
    return new Date(iso).toDateString();
  }

  private dayLabel(iso: string): string {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return this.translate.instant('support.chat.today');
    }
    if (d.toDateString() === yesterday.toDateString()) {
      return this.translate.instant('support.chat.yesterday');
    }
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'long',
      year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric',
    });
  }
}
